import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Save, Copy, Download, Shield, Users, Coins, Edit, Check, X } from 'lucide-react'
import type { SocietyResult as SocietyResultType, SocietyChangeInfo } from '@creanizer/types'
import { SocietyResultTabs } from './SocietyResultTabs'

interface SocietyResultProps {
  result: SocietyResultType
  onSave: () => void
  isSaved?: boolean
  onEdit?: (updatedResult: Partial<SocietyResultType>) => void
}

// ハイライト用ヘルパー
function useChangeHighlight(changeInfo: SocietyChangeInfo | undefined) {
  const isUpdated = (fieldPath: string): boolean => {
    return changeInfo?.updatedFields?.includes(fieldPath) || false
  }

  const isAdded = (fieldPath: string): boolean => {
    return changeInfo?.addedFields?.includes(fieldPath) || false
  }

  const isMemberAdded = (index: number): boolean => {
    return changeInfo?.addedMemberIndices?.includes(index) || false
  }

  const isEpisodeAdded = (index: number): boolean => {
    return changeInfo?.addedEpisodeIndices?.includes(index) || false
  }

  const getFieldClassName = (fieldPath: string): string => {
    if (isAdded(fieldPath)) return 'bg-green-50 dark:bg-green-950 border-l-4 border-green-500 pl-2'
    if (isUpdated(fieldPath)) return 'bg-yellow-50 dark:bg-yellow-950 border-l-4 border-yellow-500 pl-2'
    return ''
  }

  const getFieldBadge = (fieldPath: string): React.ReactNode => {
    if (isAdded(fieldPath)) {
      return <span className="ml-2 px-1.5 py-0.5 text-xs bg-green-500 text-white rounded">追加</span>
    }
    if (isUpdated(fieldPath)) {
      return <span className="ml-2 px-1.5 py-0.5 text-xs bg-yellow-500 text-white rounded">更新</span>
    }
    return null
  }

  return { isUpdated, isAdded, isMemberAdded, isEpisodeAdded, getFieldClassName, getFieldBadge }
}

export function SocietyResult({ result, onSave, isSaved = false, onEdit }: SocietyResultProps) {
  const [isEditing, setIsEditing] = useState(false)
  const changeHighlight = useChangeHighlight(result._changeInfo)
  const handleExportJson = () => {
    const exportData = {
      version: '1.0.0',
      exportDate: new Date().toISOString(),
      dataType: 'society',
      data: result
    }
    const json = JSON.stringify(exportData, null, 2)
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `society_${result.basicInfo.name}_${Date.now()}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
  
  const handleExportText = () => {
    const text = formatSocietyAsText(result)
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `society_${result.basicInfo.name}_${Date.now()}.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
  
  const handleCopy = () => {
    const text = formatSocietyAsText(result)
    navigator.clipboard.writeText(text)
  }
  
  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="space-y-2">
            <CardTitle className="text-2xl font-bold">
              {result.basicInfo.name}
            </CardTitle>
            {result.basicInfo.aliases && result.basicInfo.aliases.length > 0 && (
              <p className="text-sm text-muted-foreground">
                別名: {result.basicInfo.aliases.join('、')}
              </p>
            )}
            <div className="flex gap-4 text-sm">
              <span className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                {result.basicInfo.type}
              </span>
              <span className="flex items-center gap-1">
                <Shield className="h-4 w-4" />
                {result.basicInfo.scale}
              </span>
            </div>
            {result.basicInfo.motto && (
              <p className="text-sm italic text-muted-foreground">
                「{result.basicInfo.motto}」
              </p>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {onEdit && (
              <Button
                onClick={() => setIsEditing(!isEditing)}
                size="sm"
                variant={isEditing ? "secondary" : "outline"}
              >
                {isEditing ? (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    編集完了
                  </>
                ) : (
                  <>
                    <Edit className="h-4 w-4 mr-1" />
                    編集
                  </>
                )}
              </Button>
            )}
            <Button
              onClick={onSave}
              size="sm"
              variant={isSaved ? "secondary" : "outline"}
              disabled={isSaved}
            >
              <Save className="h-4 w-4 mr-1" />
              {isSaved ? '保存済み' : '保存'}
            </Button>
            <Button onClick={handleCopy} size="sm" variant="outline">
              <Copy className="h-4 w-4 mr-1" />
              コピー
            </Button>
            <Button onClick={handleExportText} size="sm" variant="outline">
              <Download className="h-4 w-4 mr-1" />
              TXT
            </Button>
            <Button onClick={handleExportJson} size="sm" variant="outline">
              <Download className="h-4 w-4 mr-1" />
              JSON
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          <div className="prose prose-sm max-w-none">
            <p>{result.basicInfo.summary}</p>
            {result.basicInfo.description && (
              <p className="mt-2">{result.basicInfo.description}</p>
            )}
          </div>
          
          <SocietyResultTabs
            result={result}
            isEditing={isEditing}
            onEdit={onEdit}
            changeHighlight={changeHighlight}
          />
        </div>
      </CardContent>
    </Card>
  )
}

function formatSocietyAsText(result: SocietyResultType): string {
  let text = `【組織名】${result.basicInfo.name}\n`
  
  if (result.basicInfo.aliases && result.basicInfo.aliases.length > 0) {
    text += `【別名】${result.basicInfo.aliases.join('、')}\n`
  }
  
  text += `【タイプ】${result.basicInfo.type}\n`
  text += `【規模】${result.basicInfo.scale}\n`
  
  if (result.basicInfo.foundingDate) {
    text += `【設立】${result.basicInfo.foundingDate}\n`
  }
  
  if (result.basicInfo.motto) {
    text += `【モットー】${result.basicInfo.motto}\n`
  }
  
  text += `\n【概要】\n${result.basicInfo.summary}\n`
  
  if (result.basicInfo.description) {
    text += `\n【詳細説明】\n${result.basicInfo.description}\n`
  }
  
  if (result.basicInfo.purpose) {
    text += `\n【目的・理念】\n${result.basicInfo.purpose}\n`
  }
  
  // 組織構造
  text += `\n【組織構造】\n`
  text += `メンバー数: ${result.structure.memberCount}\n`
  
  if (result.structure.hierarchy) {
    text += `\n階級制度:\n`
    result.structure.hierarchy.forEach((level) => {
      text += `  Lv${level.level}. ${level.title} (${level.count}人)\n`
      text += `    責任: ${level.responsibilities}\n`
      if (level.requirements) {
        text += `    要件: ${level.requirements}\n`
      }
    })
  }
  
  // メンバー
  if (result.members?.importantMembers && result.members.importantMembers.length > 0) {
    text += `\n【重要メンバー】\n`
    result.members.importantMembers.forEach((member, index) => {
      text += `${index + 1}. ${member.name} (${member.role})\n`
      text += `   ${member.description}\n\n`
    })
    if (result.members.notes) {
      text += `備考: ${result.members.notes}\n`
    }
  }
  
  // 特徴
  if (result.features) {
    text += `\n【特徴】\n`
    if (result.features.activities) {
      text += `\n◆活動内容\n${result.features.activities}\n`
    }
    if (result.features.culture) {
      text += `\n◆組織文化\n${result.features.culture}\n`
    }
    if (result.features.relations) {
      text += `\n◆他組織との関係\n${result.features.relations}\n`
    }
    if (result.features.secrets) {
      text += `\n◆秘密・裏設定\n${result.features.secrets}\n`
    }
  }
  
  // エピソード
  if (result.episodes?.episodes && result.episodes.episodes.length > 0) {
    text += `\n【エピソード】\n`
    result.episodes.episodes.forEach((episode) => {
      text += `◆ ${episode.title}\n`
      if (episode.source || episode.reliability) {
        if (episode.source) text += `  出典: ${episode.source}\n`
        if (episode.reliability) text += `  信憑性: ${episode.reliability}\n`
      }
      text += `  ${episode.content}\n\n`
    })
  }
  
  return text
}